import { AudioNote } from '@highlight/common'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { getChatApiRoute, getChatApiUrl } from '@shared/utils/ChatBackendUtils'
import { formatDuration, intervalToDuration } from 'date-fns'

export async function shareConversation(conversation: AudioNote): Promise<string> {
  const token = await AuthIpcRenderer.getAccessToken()

  const response = await fetch(getChatApiRoute('api/v1/meetings/share'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: conversation.id,
    }),
  })

  if (!response.ok) {
    console.warn('Failed to share conversation', response.status, await response.text())
    throw new Error('Failed to share conversation')
  }

  const BASE_API_URL = getChatApiUrl()
  const isStagingBackend = BASE_API_URL.includes('staging')
  return BASE_API_URL.includes('localhost')
    ? `http://localhost:3000/share/audio/${conversation.id}?env=local`
    : `https://highlightai.com/share/audio/${conversation.id}${isStagingBackend ? '?env=staging' : ''}`
}

export async function deleteAudioNoteShareLink(conversation: AudioNote): Promise<void> {
  const token = await AuthIpcRenderer.getAccessToken()
  const response = await fetch(getChatApiRoute(`api/v1/meetings/share/${conversation.id}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    console.warn('Failed to delete audio note share link', response.status, await response.text())
    throw new Error('Failed to delete audio note share link')
  }

  return
}

export function formatTitle(originalTitle: string | undefined) {
  if (!originalTitle) return 'Meeting Note'

  if (originalTitle.startsWith('Meeting Notes from')) {
    return 'Meeting Note'
  }

  if (originalTitle.startsWith('Google Meet')) {
    return 'Google Meet'
  }

  return originalTitle
}

export function formatConversationDuration(conversation?: AudioNote) {
  if (!conversation) return undefined

  const durationInMilliseconds = conversation?.endedAt.getTime() - conversation?.startedAt.getTime()
  const duration = intervalToDuration({ start: 0, end: durationInMilliseconds })
  const formattedDuration = formatDuration(duration, { format: ['hours', 'minutes'] })
  const replaceHoursAndMinutes = formattedDuration.replace('hour', 'hr').replace('minute', 'min')

  return replaceHoursAndMinutes
}
